import type {
  BlockComponent,
  EditorHost,
  TextRangePoint,
  TextSelection,
} from '@blocksuite/affine/block-std';
import type { NoteBlockModel } from '@blocksuite/affine/blocks';
import {
  AffineSchemas,
  defaultImageProxyMiddleware,
  embedSyncedDocMiddleware,
  isInsideEdgelessEditor,
  MarkdownAdapter,
  matchFlavours,
  NoteDisplayMode,
  PlainTextAdapter,
  titleMiddleware,
} from '@blocksuite/affine/blocks';
import { assertExists } from '@blocksuite/affine/global/utils';
import {
  type BlockModel,
  type BlockSnapshot,
  type Doc,
  DocCollection,
  type DocCollectionOptions,
  type DraftModel,
  IdGeneratorType,
  Job,
  Schema,
  Slice,
  type SliceSnapshot,
  toDraftModel,
} from '@blocksuite/affine/store';

const getNoteId = (blockElement: BlockComponent) => {
  let element = blockElement;
  while (element.flavour !== 'affine:note') {
    if (!element.parentComponent) {
      break;
    }
    element = element.parentComponent;
  }

  return element.model.id;
};

const setBlockSelection = (
  host: EditorHost,
  parent: BlockComponent,
  models: BlockModel[]
) => {
  const selections = models
    .map(model => model.id)
    .map(blockId => host.selection.create('block', { blockId }));

  if (isInsideEdgelessEditor(host)) {
    const surfaceElementId = getNoteId(parent);
    const surfaceSelection = host.selection.create(
      'surface',
      selections[0].blockId,
      [surfaceElementId],
      true
    );

    selections.push(surfaceSelection);
    host.selection.set(selections);
  } else {
    host.selection.setGroup('note', selections);
  }
};

function getSelection(host: EditorHost) {
  const textSelection = host.selection.find('text');
  const mode = textSelection ? 'flat' : 'highest';
  const { selectedBlocks } = host.command.exec('getSelectedBlocks', {
    types: ['text'],
    mode,
    textSelection,
  });
  if (!selectedBlocks) return;
  const length = selectedBlocks.length;
  const firstBlock = selectedBlocks[0];
  const lastBlock = selectedBlocks[length - 1];
  const selectedModels = selectedBlocks.map(block => block.model);
  return {
    textSelection,
    selectedModels,
    firstBlock,
    lastBlock,
  };
}

function getNoteBlockModels(doc: Doc) {
  const notes = doc
    .getBlocksByFlavour('affine:note')
    .filter(
      note =>
        (note.model as NoteBlockModel).displayMode !==
        NoteDisplayMode.EdgelessOnly
    )
    .map(note => note.model as NoteBlockModel);
  const blockModels = notes.reduce((acc, note) => {
    acc.push(...note.children);
    return acc;
  }, [] as BlockModel[]);
  return blockModels;
}

export const markdownToSnapshot = async (
  markdown: string,
  doc: Doc
  // host?: EditorHost
) => {
  const job = new Job({
    collection: doc.collection,
    middlewares: [defaultImageProxyMiddleware],
    docCRUD: {
      create: (id: string) => {
        return doc.collection.createDoc({ id });
      },
    },
  });
  const adapter = new MarkdownAdapter(job);
  const snapshot = await adapter.toSliceSnapshot({
    file: markdown,
    assets: job.assetsManager,
    pageVersion: doc.collection.meta.pageVersion ?? 0,
    workspaceVersion: doc.collection.meta.workspaceVersion ?? 0,
    workspaceId: doc.collection.id,
    pageId: doc.id,
  });

  return {
    snapshot,
    transformer: job,
  };
};

async function insertFromMarkdown(
  doc: Doc,
  markdown: string,
  parent?: string,
  index?: number
) {
  const { snapshot, transformer } = await markdownToSnapshot(markdown, doc);
  const snapshots = snapshot?.content.flatMap(x => x.children) ?? [];

  const models: BlockModel[] = [];
  for (let i = 0; i < snapshots.length; i++) {
    const blockSnapshot = snapshots[i];
    const model = await transformer.snapshotToBlock(
      blockSnapshot,
      doc,
      parent,
      (index ?? 0) + i
    );
    if (model) {
      models.push(model);
    }
  }

  return models;
}

const insert = async (
  host: EditorHost,
  content: string,
  selectBlock: BlockComponent,
  below: boolean = true
) => {
  const blockParent = selectBlock.parentComponent;
  if (!blockParent) return;
  const index = blockParent.model.children.findIndex(
    model => model.id === selectBlock.model.id
  );
  const insertIndex = below ? index + 1 : index;

  const models = await insertFromMarkdown(
    host.doc,
    content,
    blockParent.model.id,
    insertIndex
  );

  await host.updateComplete;
  requestAnimationFrame(() => setBlockSelection(host, blockParent, models));
};

const replace = async (
  host: EditorHost,
  content: string,
  firstBlock: BlockComponent,
  selectedModels: BlockModel[],
  textSelection?: TextSelection
) => {
  const firstBlockParent = firstBlock.parentComponent;
  if (!firstBlockParent) return;
  const firstIndex = firstBlockParent.model.children.findIndex(
    model => model.id === firstBlock.model.id
  );

  if (textSelection) {
    const schema = new Schema();
    schema.register(AffineSchemas);
    const options: DocCollectionOptions = {
      id: 'AI_REPLACE',
      schema,
      idGenerator: IdGeneratorType.NanoID,
      awarenessSources: [],
    };

    const collection = new DocCollection(options);
    collection.meta.initialize();
    collection.start();

    try {
      const doc = collection.createDoc({ id: 'doc:home' });
      doc.load();
      const rootId = doc.addBlock('affine:page', {});
      doc.addBlock('affine:surface', {}, rootId);
      const noteId = doc.addBlock('affine:note', {}, rootId);

      const { snapshot, transformer } = await markdownToSnapshot(content, doc);

      if (snapshot) {
        const blockSnapshots = (
          snapshot.content[0].flavour === 'affine:note'
            ? snapshot.content[0].children
            : snapshot.content
        ) as BlockSnapshot[];

        const blocks = (
          await Promise.all(
            blockSnapshots.map(async blockSnapshot => {
              return await transformer.snapshotToBlock(
                blockSnapshot,
                doc,
                noteId,
                0
              );
            })
          )
        ).filter(block => block) as BlockModel[];
        host.std.command.exec('replaceSelectedTextWithBlocks', {
          textSelection,
          blocks,
        });
      }
    } finally {
      collection.dispose();
    }
  } else {
    selectedModels.forEach(model => {
      host.doc.deleteBlock(model);
    });

    const models = await insertFromMarkdown(
      host.doc,
      content,
      firstBlockParent.model.id,
      firstIndex
    );

    await host.updateComplete;
    requestAnimationFrame(() =>
      setBlockSelection(host, firstBlockParent, models)
    );
  }
};

const insertBelow = async (
  host: EditorHost,
  content: string,
  selectBlock: BlockComponent
) => {
  await insert(host, content, selectBlock, true);
};

export const insertAbove = async (
  host: EditorHost,
  content: string,
  selectBlock: BlockComponent
) => {
  await insert(host, content, selectBlock, false);
};

export async function insertMarkdownBelow(host: EditorHost, text: string) {
  const selection = getSelection(host);
  if (!text || !selection) return;

  const { lastBlock } = selection;
  await insertBelow(host, text, lastBlock);
}

export async function insertMarkdownAbove(host: EditorHost, text: string) {
  const selection = getSelection(host);
  if (!text || !selection) return;

  const { lastBlock } = selection;
  await insertAbove(host, text, lastBlock);
}

export async function replaceWithMarkdown(host: EditorHost, text: string) {
  const selection = getSelection(host);
  if (!text || !selection) return;

  const { textSelection, firstBlock, selectedModels } = selection;
  await replace(host, text, firstBlock, selectedModels, textSelection);
}

const updateSnapshotText = (
  point: TextRangePoint,
  snapshot: BlockSnapshot,
  model: DraftModel
) => {
  const { index, length } = point;
  if (!snapshot.props.text || length === 0) {
    return;
  }
  (snapshot.props.text as Record<string, unknown>).delta =
    model.text?.sliceToDelta(index, length + index);
};

function processSnapshot(
  snapshot: BlockSnapshot,
  text: TextSelection,
  host: EditorHost
) {
  const model = host.doc.getBlockById(snapshot.id);
  assertExists(model);

  const modelId = model.id;
  if (text.from.blockId === modelId) {
    updateSnapshotText(text.from, snapshot, model);
  }
  if (text.to && text.to.blockId === modelId) {
    updateSnapshotText(text.to, snapshot, model);
  }

  // If the snapshot has children, handle them recursively
  snapshot.children.forEach(childSnapshot =>
    processSnapshot(childSnapshot, text, host)
  );
}

/**
 * Processes the text in the given snapshot if there is a text selection.
 * Only the selected portion of the snapshot will be processed.
 */
function processTextInSnapshot(snapshot: SliceSnapshot, host: EditorHost) {
  const { content } = snapshot;
  const text = host.selection.find('text');
  if (!content.length || !text) return;

  content.forEach(snapshot => processSnapshot(snapshot, text, host));
}

export function getSelectedModels(editorHost: EditorHost) {
  const { selectedModels } = editorHost.std.command.exec('getSelectedModels', {
    types: ['block', 'text'],
  });
  return selectedModels;
}

export async function getContentFromSlice(
  host: EditorHost,
  slice: Slice,
  type: 'markdown' | 'plain-text' = 'markdown'
) {
  const job = new Job({
    collection: host.std.doc.collection,
    middlewares: [titleMiddleware, embedSyncedDocMiddleware('content')],
    docCRUD: {
      create: (id: string) => {
        return host.std.doc.collection.createDoc({ id });
      },
    },
  });
  const snapshot = await job.sliceToSnapshot(slice);
  if (!snapshot) {
    return '';
  }
  processTextInSnapshot(snapshot, host);
  const adapter =
    type === 'markdown' ? new MarkdownAdapter(job) : new PlainTextAdapter(job);
  const content = await adapter.fromSliceSnapshot({
    snapshot,
    assets: job.assetsManager,
  });
  return content.file;
}

function traverse(model: DraftModel, drafts: DraftModel[]) {
  const isDatabase = model.flavour === 'affine:database';
  const children = isDatabase
    ? model.children
    : model.children.filter(child => {
        const idx = drafts.findIndex(m => m.id === child.id);
        return idx >= 0;
      });

  children.forEach(child => {
    const idx = drafts.findIndex(m => m.id === child.id);
    if (idx >= 0) {
      drafts.splice(idx, 1);
    }
    traverse(child, drafts);
  });
  model.children = children;
}

export async function getTextContentFromBlockModels(
  editorHost: EditorHost,
  models: BlockModel[],
  type: 'markdown' | 'plain-text' = 'markdown'
) {
  // Currently only filter out images and databases
  const selectedTextModels = models.filter(
    model => !matchFlavours(model, ['affine:image', 'affine:database'])
  );
  const drafts = selectedTextModels.map(toDraftModel);
  drafts.forEach(draft => traverse(draft, drafts));
  const slice = Slice.fromModels(editorHost.std.doc, drafts);
  return getContentFromSlice(editorHost, slice, type);
}

export async function extractMarkdownFromDoc(
  host: EditorHost
): Promise<{ text: string; selection: boolean }> {
  const selection = getSelection(host);

  const isCollapsed = selection?.textSelection?.isCollapsed();

  const blockModels = isCollapsed
    ? getNoteBlockModels(host.doc)
    : getSelectedModels(host);

  if (!blockModels) return { text: '&nbsp;', selection: false };

  const text = await getTextContentFromBlockModels(
    host,
    blockModels,
    'markdown'
  );

  const content = {
    text: text === '' ? '&nbsp;' : text,
    selection: !isCollapsed,
  };

  return content;
}
